import React, { useEffect, useRef } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, CrosshairMode } from "lightweight-charts";

export const BAND_STYLE = {
  b34: { color: "#3b82f6", fill: "rgba(59,130,246,0.14)", label: "TFR Band" },
  b200: { color: "#f59e0b", fill: "rgba(245,158,11,0.12)", label: "TFR Macro Band" },
};

export const VOL_STYLE = {
  big: { color: "#f59e0b", label: "Big Money Volume ≥4×" },
  huge: { color: "#c026d3", label: "Big Money Volume ≥6.5×" },
};
const volColor = (c, x) => (x >= 6.5 ? VOL_STYLE.huge.color : x >= 4 ? VOL_STYLE.big.color
  : c.c >= c.o ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)");

const IST = 19800; // show exchange (IST) clock on the time axis
export const toTime = (t) => (t.length > 10 ? Math.floor(Date.parse(t) / 1000) + IST : t);

const pad = (n) => String(n).padStart(2, "0");
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const asDate = (t) => (typeof t === "number" ? new Date(t * 1000)
  : typeof t === "string" ? new Date(`${t}T00:00:00Z`) : new Date(Date.UTC(t.year, t.month - 1, t.day)));
const fmtTick = (t, type) => {
  const d = asDate(t);
  if (type >= 3) return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  if (type === 2) return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
  return type === 1 ? MON[d.getUTCMonth()] : String(d.getUTCFullYear());
};
const fmtTime = (t) => {
  const d = asDate(t);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

// Series primitive that shades the area between a band's upper and lower line.
class BandFill {
  constructor(fill) { this.fill = fill; this.pts = []; }
  attached({ chart, series, requestUpdate }) { this.chart = chart; this.series = series; this.requestUpdate = requestUpdate; }
  setData(pts) { this.pts = pts; this.requestUpdate?.(); }
  paneViews() {
    const self = this;
    return [{
      zOrder: () => "bottom",
      renderer: () => ({
        draw: (target) => target.useMediaCoordinateSpace(({ context }) => {
          const ts = self.chart.timeScale();
          const xy = self.pts.map((p) => [ts.timeToCoordinate(p.time), self.series.priceToCoordinate(p.hi), self.series.priceToCoordinate(p.lo)])
            .filter(([x, a, b]) => x !== null && a !== null && b !== null);
          if (xy.length < 2) return;
          context.beginPath();
          xy.forEach(([x, a], i) => (i ? context.lineTo(x, a) : context.moveTo(x, a)));
          for (let i = xy.length - 1; i >= 0; i -= 1) context.lineTo(xy[i][0], xy[i][2]);
          context.closePath();
          context.fillStyle = self.fill;
          context.fill();
        }),
      }),
    }];
  }
}

// Fast canvas chart (TradingView lightweight-charts): candles + TFR Band (34) + TFR Macro Band (200).
export function CandleChart({ candles = [], bands = {}, height = 480, live = null, tfMinutes = 5, levels = [] }) {
  const box = useRef(null);
  const api = useRef({});

  useEffect(() => {
    const chart = createChart(box.current, {
      height, autoSize: true,
      layout: { background: { color: "#ffffff" }, textColor: "#64748b", fontFamily: "JetBrains Mono", fontSize: 11 },
      grid: { vertLines: { color: "#f1f5f9" }, horzLines: { color: "#f1f5f9" } },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: "#94a3b8" }, horzLine: { color: "#94a3b8" } },
      localization: { locale: "en-IN", timeFormatter: fmtTime, priceFormatter: (p) => p.toFixed(2) },
      rightPriceScale: { borderColor: "#e2e8f0" },
      timeScale: { borderColor: "#e2e8f0", timeVisible: true, secondsVisible: false, rightOffset: 4, tickMarkFormatter: fmtTick },
    });
    const mk = (key) => {
      const st = BAND_STYLE[key];
      const o = { color: st.color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false };
      return { hi: chart.addSeries(LineSeries, o), lo: chart.addSeries(LineSeries, o) };
    };
    const vol = chart.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" }, priceLineVisible: false, lastValueVisible: false });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    chart.priceScale("right").applyOptions({ scaleMargins: { top: 0.06, bottom: 0.24 } });
    const b200 = mk("b200");
    const b34 = mk("b34");
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: "#16a34a", downColor: "#dc2626", borderVisible: false, wickUpColor: "#16a34a", wickDownColor: "#dc2626",
    });
    const fills = { b200: new BandFill(BAND_STYLE.b200.fill), b34: new BandFill(BAND_STYLE.b34.fill) };
    candle.attachPrimitive(fills.b200);
    candle.attachPrimitive(fills.b34);
    api.current = { chart, candle, b34, b200, fills, vol };
    return () => chart.remove();
  }, [height]);

  useEffect(() => {
    const { candle, chart, fills } = api.current;
    if (!candle) return;
    const data = candles.map((c) => ({ time: toTime(c.t), open: c.o, high: c.h, low: c.l, close: c.c }));
    candle.setData(data);
    // volume vs 20-bar average: Big Money Volume candles highlighted
    api.current.vol.setData(candles.map((c, i) => {
      const prev = candles.slice(Math.max(0, i - 20), i).map((b) => b.v || 0);
      const avg = prev.length >= 20 ? prev.reduce((a, b) => a + b, 0) / prev.length : 0;
      return { time: data[i].time, value: c.v || 0, color: volColor(c, avg ? (c.v || 0) / avg : 0) };
    }));
    ["b34", "b200"].forEach((k) => {
      const hi = bands[`${k}_hi`] || [], lo = bands[`${k}_lo`] || [];
      const off = data.length - hi.length;
      const pts = hi.map((v, i) => ({ time: data[i + off]?.time, hi: v, lo: lo[i] })).filter((p) => p.time !== undefined);
      api.current[k].hi.setData(pts.map((p) => ({ time: p.time, value: p.hi })));
      api.current[k].lo.setData(pts.map((p) => ({ time: p.time, value: p.lo })));
      fills[k].setData(pts);
    });
    if (data.length) chart.timeScale().setVisibleLogicalRange({ from: Math.max(0, data.length - 130), to: data.length + 3 });
    api.current.last = data[data.length - 1] || null;
  }, [candles, bands]);

  // horizontal level lines (TFR D / TFR W / ADR(14)) like the TradingView script
  useEffect(() => {
    const { candle } = api.current;
    if (!candle) return;
    (api.current.lines || []).forEach((l) => candle.removePriceLine(l));
    api.current.lines = levels.filter((l) => l.price).map((l) => candle.createPriceLine({
      price: l.price, color: l.color, lineWidth: l.width || 1, lineStyle: l.style ?? 0, axisLabelVisible: true, title: l.title,
    }));
  }, [levels]);

  // live tick: update the forming candle, or start a new one when a new bucket begins
  useEffect(() => {
    const { candle, last } = api.current;
    if (!candle || !last || !live?.ltp || !live?.at) return;
    const step = tfMinutes * 60;
    const off = 9 * 3600 + 15 * 60; // NSE bars start 09:15 IST
    const bucket = Math.floor((Math.floor(Date.parse(live.at) / 1000) + IST - off) / step) * step + off;
    const p = live.ltp;
    const bar = bucket > last.time
      ? { time: bucket, open: last.close, high: Math.max(last.close, p), low: Math.min(last.close, p), close: p }
      : { ...last, high: Math.max(last.high, p), low: Math.min(last.low, p), close: p };
    if (bucket < last.time) return;
    candle.update(bar);
    api.current.last = bar;
  }, [live, tfMinutes]);

  return (
    <div className="relative w-full" style={{ height }}>
      <div ref={box} className="w-full h-full" data-testid="candle-chart" />
      {!candles.length && (
        <div className="absolute inset-0 grid place-items-center bg-white text-slate-500 text-sm" data-testid="chart-empty">No chart data for this timeframe yet</div>
      )}
    </div>
  );
}
